import React, { useEffect, useState, useRef } from 'react';
import socket from '../../../../Socket';
import C from '../../../../Constant';
import { decode, Game } from '../../../../Helper';
import storage from '../../../../Storage';




const MarketTicker = ({ symbol, onSymbolChange }) => {
    const dropdownRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const initialType = symbol && ['BTC', 'ETH'].some(c => symbol.includes(c)) ? 'Crypto' : 'Currency';
    const [markets, setMarkets] = useState(symbol ? [{ ticker: symbol, type: initialType }] : []);
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState('All');
    const [ticker, setTicker] = useState({
        symbol: symbol,
        price: '0.00',
        change: '0.00',
        isUp: true
    });

    // 1. Initial Ticker List Load (Once on mount) - just fetch the list, no bulk subscribe
    useEffect(() => {
        const handleTickerList = (data) => {
            const decoded = decode(data);
            if (decoded && Array.isArray(decoded.tickers)) {
                // Map new structure { ticker, type } or fallback to strings
                const formattedMarkets = decoded.tickers.map(item => {
                    if (typeof item === 'object') {
                        return { ticker: item.ticker, type: item.type || 'Markets' };
                    }
                    const type = typeof item === 'string' && ['BTC', 'ETH'].some(c => item.includes(c)) ? 'Crypto' : 'Currency';
                    return { ticker: item, type };
                });
                setMarkets(formattedMarkets);
                // No bulk subscribe — the [symbol] effect handles subscribing to the selected ticker only
            }
        };

        socket.on(C.TICKER_LIST, handleTickerList);
        socket.emit(C.TICKER_LIST);

        return () => {
            socket.off(C.TICKER_LIST, handleTickerList);
        };
    }, []);

    // 2. Active Symbol Ticker Updates (When symbol changes)
    useEffect(() => {
        if (!symbol) return;

        // console.log(`[SUBSCRIPTION RESTART] for ${symbol}`);

        socket.emit(C.TICKER_SUBSCRIBE, { ticker: symbol }, (response) => {
            console.log(`[TICKER_SUBSCRIBE RESPONSE] for ${ticker}:`, response);
        });

        const handleTickerUpdate = (data) => {
            const decoded = decode(data);
            // console.log(`[TICKER UPDATE] for ${symbol}:`, decoded);

            if (decoded && decoded.ticker === symbol) {
                if (decoded.data) {
                    const { open, close } = decoded.data;
                    Game['current_amount'] = close;
                    const changePercent = ((close - open) / open * 100);
                    setTicker({
                        symbol: decoded.ticker,
                        price: parseFloat(close).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                        change: changePercent.toFixed(2),
                        isUp: changePercent >= 0
                    });
                }
            }
        };

        socket.on(C.TICKER_UPDATE, handleTickerUpdate);

        return () => {
            socket.off(C.TICKER_UPDATE, handleTickerUpdate);
        };
    }, [symbol]);


    const handleSelectChange = (newSymbol) => {
        if (onSymbolChange) {
            onSymbolChange(newSymbol);
        }
        setTicker(prev => ({ ...prev, price: '---', change: '0.00' }));
        setIsOpen(false);
        setSearchQuery('');
    };

    const uniqueTypes = ['All', ...new Set(markets.map(m => m.type || 'Markets'))];

    const filteredMarkets = markets.filter(m => {
        const matchesFilter = activeFilter === 'All' || m.type === activeFilter || (!m.type && activeFilter === 'Markets');
        const matchesSearch = m.ticker.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesFilter && matchesSearch;
    });

    return (
        <div className="market-ticker-container" ref={dropdownRef}>
            <div className="ticker-content">
                <div className="symbol-selector-wrapper">
                    <div
                        className="symbol-select"
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        {symbol ? symbol.replace('-', ' / ') : 'Select'} <span className="dropdown-arrow">▼</span>
                    </div>
                    {isOpen && (
                        <div className="custom-dropdown-menu" style={{ zIndex: 9999999 }}>
                            <div className="dropdown-header">
                                <div className="dropdown-title-row">
                                    <span className="dropdown-title">Assets</span>
                                    <button className="close-btn" onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}>✕</button>
                                </div>
                                <div className="search-box">
                                    <span className="search-icon">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Search"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                                <div className="filter-tabs">
                                    {uniqueTypes.map(type => (
                                        <div
                                            key={type}
                                            className={`filter-tab ${activeFilter === type ? 'active' : ''}`}
                                            onClick={(e) => { e.stopPropagation(); setActiveFilter(type); }}
                                        >
                                            {type}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="dropdown-list">
                                {filteredMarkets.length > 0 ? (
                                    filteredMarkets.map((m, i) => (
                                        <div
                                            key={i}
                                            className={`dropdown-item ${symbol === m.ticker ? 'active' : ''}`}
                                            onClick={() => handleSelectChange(m.ticker)}
                                        >
                                            <div className="asset-info">
                                                <div className="asset-icon">
                                                    {m.type === 'Crypto' ? '₿' : (m.ticker.includes('USD') || m.ticker.includes('EUR') || m.ticker.includes('GBP') || m.ticker.includes('CAD') || m.ticker.includes('JPY') || m.ticker.includes('AUD') ? '💵' : '📊')}
                                                </div>
                                                <span className="asset-name">{m.ticker.replace('-', ' / ')}</span>
                                            </div>
                                            <div className="asset-stats">
                                                <span className="star-icon">★</span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-results">No markets found</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                {/* <div className="price-display">
                    <span className="ticker-price">{ticker.price}</span>
                    <span className={`ticker-change ${ticker.isUp ? 'text-success' : 'text-danger'}`}>
                        {ticker.isUp ? '▲' : '▼'} {Math.abs(ticker.change)}%
                    </span>
                </div> */}
            </div>
            <style jsx>{`
                .market-ticker-container {
                    display: flex;
                    align-items: center;
                    background: transparent;
                    padding: 0;
                    margin: 0;
                    font-family: 'Inter', sans-serif;
                    overflow: visible;
                    width: 100%;
                    position: relative;
                }
                .ticker-content {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    width: 100%;
                    justify-content: space-between;
                }
                .symbol-selector-wrapper {
                   position: relative;
                }
                .symbol-select {
                   border-radius: 4px;
                   color: #fff;
                   font-size: 14px;
                   font-weight: 700;
                   padding: 2px 4px;
                   text-transform: uppercase;
                   cursor: pointer;
                   display: flex;
                   align-items: center;
                   gap: 6px;
                   user-select: none;
                }
                .dropdown-arrow {
                   font-size: 10px;
                   opacity: 0.7;
                }
                .custom-dropdown-menu {
                   position: absolute;
                   top: 100%;
                   left: 0;
                   margin-top: 4px;
                   background: #0b1527;
                   border: 1px solid rgba(255, 255, 255, 0.08);
                   border-radius: 8px;
                   box-shadow: 0 12px 32px rgba(0,0,0,0.6);
                   min-width: 340px;
                   width: 340px;
                   z-index: 9999999;
                   display: flex;
                   flex-direction: column;
                   overflow: hidden;
                }
                .dropdown-header {
                   padding: 16px;
                   border-bottom: 1px solid rgba(255, 255, 255, 0.05);
                }
                .dropdown-title-row {
                   display: flex;
                   justify-content: space-between;
                   align-items: center;
                   margin-bottom: 16px;
                }
                .dropdown-title {
                   color: #fff;
                   font-size: 18px;
                   font-weight: 700;
                }
                .close-btn {
                   background: none;
                   border: none;
                   color: #8c9baf;
                   font-size: 16px;
                   cursor: pointer;
                   padding: 4px;
                   outline: none;
                   transition: color 0.2s;
                }
                .close-btn:hover {
                   color: #fff;
                }
                .search-box {
                   position: relative;
                   margin-bottom: 16px;
                }
                .search-icon {
                   position: absolute;
                   left: 14px;
                   top: 50%;
                   transform: translateY(-50%);
                   color: #6b829e;
                   display: flex;
                   align-items: center;
                   pointer-events: none;
                }
                .search-box input {
                   width: 100%;
                   background: #152238;
                   border: 1px solid transparent;
                   border-radius: 6px;
                   padding: 10px 14px 10px 38px;
                   color: #fff;
                   font-size: 14px;
                   outline: none;
                   transition: all 0.2s;
                }
                .search-box input::placeholder {
                   color: #6b829e;
                }
                .search-box input:focus {
                   border-color: rgba(74, 159, 245, 0.5);
                   background: #1a2a44;
                }
                .filter-tabs {
                   display: flex;
                   gap: 8px;
                   overflow-x: auto;
                   padding-bottom: 4px;
                }
                .filter-tabs::-webkit-scrollbar {
                   height: 0px;
                }
                .filter-tab {
                   padding: 6px 14px;
                   border-radius: 100px;
                   font-size: 12px;
                   font-weight: 500;
                   color: #b0c4de;
                   background: rgba(255, 255, 255, 0.05);
                   cursor: pointer;
                   white-space: nowrap;
                   transition: all 0.2s ease;
                   border: 1px solid transparent;
                }
                .filter-tab:hover {
                   color: #fff;
                   background: rgba(255, 255, 255, 0.08);
                }
                .filter-tab.active {
                   background: #b3440d;
                   color: #fff;
                }
                .dropdown-list {
                   max-height: 380px;
                   overflow-y: auto;
                   padding: 8px 0;
                }
                .dropdown-list::-webkit-scrollbar {
                   width: 6px;
                }
                .dropdown-list::-webkit-scrollbar-thumb {
                   background: rgba(255, 255, 255, 0.1);
                   border-radius: 6px;
                }
                .dropdown-item {
                   padding: 12px 16px;
                   display: flex;
                   justify-content: space-between;
                   align-items: center;
                   cursor: pointer;
                   transition: all 0.15s ease;
                   border-left: 3px solid transparent;
                }
                .dropdown-item:hover {
                   background: rgba(255, 255, 255, 0.03);
                }
                .dropdown-item.active {
                   background: rgba(74, 159, 245, 0.1);
                   border-left-color: #4a9ff5;
                }
                .asset-info {
                   display: flex;
                   align-items: center;
                   gap: 12px;
                }
                .asset-icon {
                   background: rgba(255, 255, 255, 0.05);
                   width: 24px;
                   height: 24px;
                   border-radius: 50%;
                   display: flex;
                   align-items: center;
                   justify-content: center;
                   font-size: 12px;
                }
                .asset-name {
                   color: #fff;
                   font-size: 13px;
                   font-weight: 600;
                }
                .asset-stats {
                   display: flex;
                   align-items: center;
                   gap: 12px;
                }
                .payout-rate {
                   color: #00c853;
                   font-size: 13px;
                   font-weight: 500;
                }
                .star-icon {
                   color: #6b829e;
                   font-size: 14px;
                   transition: color 0.2s;
                }
                .star-icon:hover, .dropdown-item.active .star-icon {
                   color: #f26722;
                }
                .no-results {
                   padding: 24px;
                   text-align: center;
                   color: #6b829e;
                   font-size: 13px;
                }
                .price-display {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .ticker-price {
                    color: #fff;
                    font-size: 14px;
                    font-weight: 700;
                    font-family: 'Roboto Mono', monospace;
                }
                .ticker-change {
                    font-size: 11px;
                    font-weight: 700;
                }
            `}</style>
        </div>
    );
};

export default MarketTicker;
